// useLiveFollower — the single render source for live sessions (issue #28).
//
// Host AND guests are followers: the server's timeline runner owns status,
// block index, and absolute deadlines (live:snapshot); this hook turns the
// latest snapshot into a coachState-compatible object so the existing
// useWorkoutUiState → WorkoutCoachView pipeline renders it unchanged. A
// 250 ms cosmetic interval recomputes countdowns from absolute timestamps
// (offset-corrected), so timers never drift and survive tab throttling.
//
// Each participant resolves the block's tree path against their OWN plan
// (localPlanData carries AI equipment substitutions), keeps their own block
// log, and builds their own history payload — per-participant records.

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useSocial } from "../contexts/SocialContext";
import { useAuth } from "../contexts/AuthContext";
import useSound from "./useSound";
import {
  deriveCountdowns,
  computeLiveProgress,
  resolveBlockNode,
} from "../utils/liveTimeline";
import { amendReps, createPhaseLogEntries, doneRepsSets, lastRepsSet } from "../utils/phaseLog";
import { MIDWAY_BEEP_MIN_SECONDS } from "../config/soundProfiles";

const RUNNING_STATUSES = new Set(["active", "waiting_for_sync"]);

export default function useLiveFollower() {
  const { liveSession, getServerNow } = useSocial();
  const { user } = useAuth();
  const playSound = useSound();

  const { snapshot, planData, localPlanData, isHost, session } = liveSession;
  const myPlan = isHost ? planData : localPlanData || planData;

  // ── Cosmetic tick (recompute countdowns from absolute deadlines) ──
  const [, forceTick] = useReducer((c) => c + 1, 0);
  const isRunning = !!snapshot && RUNNING_STATUSES.has(snapshot.status);

  // ── Participation tracking ────────────────────────────────
  // elapsed accumulates only while the session runs for THIS client, so a
  // late joiner's history record reflects their own participation.
  const elapsedRef = useRef(0);
  const lastTickAtRef = useRef(null);
  const startedAtIsoRef = useRef(null);
  const joinedAtBlockIndexRef = useRef(null);
  const phaseStartElapsedRef = useRef(0);
  const setStartElapsedRef = useRef(0);
  const blockLogRef = useRef([]);
  // A solo session that went live partway through hands its phase log over, so
  // the record saved at the end is the whole workout rather than the shared
  // tail of one (#81). Seeded once: both sides build entries with
  // createPhaseLogEntry, so this really is just a concatenation.
  const carriedLogRef = useRef(false);
  if (!carriedLogRef.current && liveSession?.priorPhaseLog?.length) {
    carriedLogRef.current = true;
    blockLogRef.current = [...liveSession.priorPhaseLog, ...blockLogRef.current];
  }
  const prevBlockRef = useRef(null); // { key, node, setKey, loopCounters }
  const prevCountInRef = useRef(0);
  const prevTimeLeftRef = useRef(null);
  const halfwayFiredKeyRef = useRef(null); // block.key already given a midway beep

  useEffect(() => {
    if (!isRunning) {
      lastTickAtRef.current = null;
      return;
    }
    if (startedAtIsoRef.current == null) {
      startedAtIsoRef.current = new Date().toISOString();
      joinedAtBlockIndexRef.current = snapshot?.blockIndex ?? 0;
    }
    lastTickAtRef.current = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      if (lastTickAtRef.current != null) {
        elapsedRef.current += (now - lastTickAtRef.current) / 1000;
      }
      lastTickAtRef.current = now;
      forceTick();
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  // ── Block transition bookkeeping (phase log + sound cues) ──
  const blockKey = snapshot?.block?.key || null;
  useEffect(() => {
    if (!snapshot) return;
    const prev = prevBlockRef.current;
    // A mid-session edit (#175) can move the block in progress to a new path
    // (a finished step before it was deleted), which changes its key without
    // changing the block. Same step, same round: just follow it, no log entry
    // and no cue.
    if (blockKey && prev && prev.key !== blockKey) {
      const node = resolveBlockNode(myPlan, snapshot.block);
      const loopCounters = JSON.stringify(snapshot.block.loopCounters || {});
      if (
        node?.id != null &&
        node.id === prev.node?.id &&
        loopCounters === prev.loopCounters
      ) {
        const setKey = `${(snapshot.block.path || []).slice(0, -2).join(",")}#${loopCounters}`;
        prevBlockRef.current = { ...prev, key: blockKey, node, setKey };
        return;
      }
    }
    if (blockKey && prev?.key !== blockKey) {
      // Close out the previous block in the participant's own log.
      if (prev?.node) {
        blockLogRef.current.push(...createPhaseLogEntries(
          prev.node,
          elapsedRef.current - phaseStartElapsedRef.current,
        ));
      }
      phaseStartElapsedRef.current = elapsedRef.current;

      // Per-set resolved — the log records the reps of the set just finished,
      // not set 1's for every set of a ramp.
      const node = resolveBlockNode(myPlan, snapshot.block);
      const setKey = `${(snapshot.block.path || []).slice(0, -2).join(",")}#${JSON.stringify(snapshot.block.loopCounters || {})}`;
      if (prev && prev.setKey !== setKey) {
        setStartElapsedRef.current = elapsedRef.current;
      }
      prevBlockRef.current = {
        key: blockKey,
        node,
        setKey,
        loopCounters: JSON.stringify(snapshot.block.loopCounters || {}),
      };

      // Local audio cue on entering the new block (guests get sound now too).
      if (prev) {
        const t = String(node?.type || "").toLowerCase();
        if (t.includes("rest")) playSound("rest");
        else if (t.includes("warmup")) playSound("warmup");
        else if (t.includes("cooldown")) playSound("cooldown");
        else playSound("work");
      }
    }
    if (snapshot.status === "ended" && prevBlockRef.current?.node) {
      // Final block: bank whatever time was spent in it.
      blockLogRef.current.push(...createPhaseLogEntries(
        prevBlockRef.current.node,
        elapsedRef.current - phaseStartElapsedRef.current,
      ));
      prevBlockRef.current = null;
      playSound("end");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockKey, snapshot?.status]);

  // ── Countdown derivation ──────────────────────────────────
  const nowServer = getServerNow();
  const derive = deriveCountdowns(snapshot, nowServer);
  const { countInRemaining, timeLeftInPhase, repCapRemaining } = derive;

  // Count-in / final-3-seconds ticks (cosmetic, locally driven).
  useEffect(() => {
    if (
      countInRemaining > 0 &&
      countInRemaining <= 3 &&
      countInRemaining !== prevCountInRef.current
    ) {
      playSound("countdown_tick");
    }
    prevCountInRef.current = countInRemaining;
  }, [countInRemaining, playSound]);
  useEffect(() => {
    if (
      snapshot?.status === "active" &&
      snapshot?.block?.type === "timed" &&
      timeLeftInPhase > 0 &&
      timeLeftInPhase <= 3 &&
      timeLeftInPhase !== prevTimeLeftRef.current
    ) {
      playSound("countdown_tick");
    }
    prevTimeLeftRef.current = timeLeftInPhase;
  }, [timeLeftInPhase, snapshot?.status, snapshot?.block?.type, playSound]);

  // Midway beep at the halfway mark of a long-enough timed block. Mirrors the
  // solo coach's halfway cue (same MIDWAY_BEEP_MIN_SECONDS gate) — live sessions
  // previously had no midway beep at all. Fired once per block via the key guard
  // so the 250 ms cosmetic ticks can't repeat it.
  const blockDurationSec = snapshot?.block?.durationSec ?? 0;
  useEffect(() => {
    const key = snapshot?.block?.key || null;
    if (
      key &&
      snapshot?.status === "active" &&
      snapshot?.block?.type === "timed" &&
      blockDurationSec > MIDWAY_BEEP_MIN_SECONDS &&
      timeLeftInPhase > 0 &&
      timeLeftInPhase <= Math.floor(blockDurationSec / 2) &&
      halfwayFiredKeyRef.current !== key
    ) {
      halfwayFiredKeyRef.current = key;
      playSound("halfway");
    }
  }, [
    timeLeftInPhase,
    snapshot?.status,
    snapshot?.block?.type,
    snapshot?.block?.key,
    blockDurationSec,
    playSound,
  ]);

  // ── Synthesised coachState (useWorkoutUiState-compatible) ──
  const block = snapshot?.block || null;
  // Both resolved for the set the block is running (its loopCounters), so a
  // ramped block shows the working reps and load of the set in progress rather
  // than the authored set-1 mirror.
  const currentNode = useMemo(
    () => resolveBlockNode(myPlan, block),
    [block, myPlan],
  );
  const nextUpNode = useMemo(
    () => resolveBlockNode(myPlan, snapshot?.nextBlock),
    [snapshot?.nextBlock, myPlan],
  );
  const progress = useMemo(
    () => computeLiveProgress(myPlan, block),
    [myPlan, block],
  );

  const uiStatus =
    snapshot?.status === "paused"
      ? "paused"
      : snapshot?.status === "ended"
        ? "finished"
        : "running";
  const isHolding =
    snapshot?.awaitingReady === true && snapshot?.status === "waiting_for_sync";

  const coachState = useMemo(
    () => ({
      status: uiStatus,
      currentNode,
      nextUpNode,
      nextUpIsTimed: snapshot?.nextBlock?.type === "timed",
      path: block?.path || [],
      loopCounters: block?.loopCounters || {},
      timeLeftInPhase,
      progress,
      elapsedSessionTime: Math.round(elapsedRef.current),
      phaseStartElapsed: phaseStartElapsedRef.current,
      setStartElapsed: setStartElapsedRef.current,
      canGoBack: (snapshot?.blockIndex ?? 0) > 0,
      isLast:
        !!snapshot &&
        snapshot.blockCount != null &&
        snapshot.blockIndex === snapshot.blockCount - 1,
      countUpMode: false,
      // A changeover hold (#81 follow-up): the server keeps the GET READY
      // window open for readyHoldSeconds and a tap cuts it short, so the
      // count-up is simply the part of that window already spent.
      // A hold that is paused shows the ordinary pause screen, as solo does.
      isReadyGate: isHolding,
      readyGateElapsed:
        isHolding
          ? Math.max(
              0,
              (snapshot.readyHoldSeconds ?? 300) - (countInRemaining || 0),
            )
          : 0,
      faffTotal: 0,
    }),
    [
      uiStatus,
      currentNode,
      nextUpNode,
      block,
      timeLeftInPhase,
      countInRemaining,
      progress,
      snapshot,
      isHolding,
    ],
  );

  const phaseLogRef = blockLogRef;

  // Reps actually done (#144), the same correction the offline Coach offers
  // (#97): this participant's own log, so a correction is theirs alone and
  // rides into the session they save.
  const [repsEdits, bumpRepsEdits] = useReducer((n) => n + 1, 0);
  const getLastRepsSet = useCallback(
    () => lastRepsSet(blockLogRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repsEdits],
  );
  const getDoneRepsSets = useCallback(
    () => doneRepsSets(blockLogRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repsEdits],
  );
  const amendSetReps = useCallback((index, reps) => {
    if (!amendReps(blockLogRef.current, index, reps)) return false;
    bumpRepsEdits();
    return true;
  }, []);

  // ── Rep barrier view ──────────────────────────────────────
  const isRepBlock = block?.type === "reps";
  const readyUserIds = snapshot?.readyUserIds || [];
  const hasMarkedDone = !!user && readyUserIds.includes(user.id);

  // ── Per-participant history payload ───────────────────────
  // `plan` stands in for the session's plan, as edited at the finish: a Free
  // training block kept going into is saved with what was logged (#199).
  const buildSessionPayload = (feedback = {}, { plan = myPlan } = {}) => {
    const partial = snapshot?.status !== "ended";
    return {
      started_at: startedAtIsoRef.current || new Date().toISOString(),
      ended_at: new Date().toISOString(),
      duration_minutes: Math.max(1, Math.round(elapsedRef.current / 60)),
      workout_type: plan?.label || plan?.type || "Live workout",
      rating: feedback?.rpe ?? null,
      notes: feedback?.notes ?? "",
      plan_data: {
        ...(plan || {}),
        phase_log: [...blockLogRef.current],
        live: {
          partial,
          joined_at_block_index: joinedAtBlockIndexRef.current ?? 0,
          // Where a mid-workout go-live happened, as distinct from where a
          // guest joined someone else's session.
          ...(liveSession?.priorPhaseLog?.length
            ? { went_live_at_block_index: joinedAtBlockIndexRef.current ?? 0 }
            : {}),
          completed_blocks: blockLogRef.current.length,
          block_count: snapshot?.blockCount ?? null,
          ended_reason: snapshot?.endedReason || null,
          is_host: isHost,
        },
      },
      live_session_id: snapshot?.liveSessionId || session?.id || null,
    };
  };

  return {
    snapshot,
    session,
    isHost,
    myPlan,
    coachState,
    currentNode,
    countInRemaining,
    countInTotal: snapshot?.settings?.countInSeconds ?? 3,
    timeLeftInPhase,
    repCapRemaining,
    repCapPaused: snapshot?.repCapPaused === true,
    repCapPausedRemaining:
      snapshot?.repCapPausedRemainingMs != null
        ? Math.max(0, Math.ceil(snapshot.repCapPausedRemainingMs / 1000))
        : null,
    isRepBlock,
    hasMarkedDone,
    // Is the all-hands barrier actually in force? False when you are the only
    // participant (a broadcast, or a session nobody joined), where the block
    // waits on you alone and there is nothing to wait *for*.
    syncEnabled: snapshot?.syncEnabled === true,
    readyUserIds,
    hostConnected: snapshot?.hostConnected !== false,
    hostGraceEndsAt: snapshot?.hostGraceEndsAt || null,
    blockIndex: snapshot?.blockIndex ?? 0,
    phaseLogRef,
    getLastRepsSet,
    getDoneRepsSets,
    amendSetReps,
    elapsedSessionTime: Math.round(elapsedRef.current),
    // When the block in progress began, read fresh each render: the memoised
    // coachState can hold the previous block's for a render after a change.
    phaseStartElapsed: phaseStartElapsedRef.current,
    buildSessionPayload,
    isSubstituted: !!currentNode?._substituted,
  };
}
